import { useCallback } from 'react'

import { Experiments } from 'constants/optimizely'
import useExperiment from 'hooks/Optimizely/useExperiment'

export enum CroSppCollapsedPackagesVariant {
  control = 'control',
  collapsed = 'collapsed',
  expanded = 'expanded',
}

const CRO_SPP_COLLAPSED_PACKAGES_VARIANTS = new Set<CroSppCollapsedPackagesVariant>([
  CroSppCollapsedPackagesVariant.control,
  CroSppCollapsedPackagesVariant.collapsed,
  CroSppCollapsedPackagesVariant.expanded,
])

interface CroSppCollapsedPackagesExperiment {
  getRoomPackagesVariant: (roomPosition: number) => CroSppCollapsedPackagesVariant
}

interface Options {
  disabled?: boolean
}

export function getCroSppCollapsedPackagesVariant(
  variation: string | undefined,
): CroSppCollapsedPackagesVariant | undefined {
  const packagesVariant = variation as CroSppCollapsedPackagesVariant | undefined
  return packagesVariant && CRO_SPP_COLLAPSED_PACKAGES_VARIANTS.has(packagesVariant) ? packagesVariant : undefined
}

export function getCroSppCollapsedPackagesRoomVariant(
  variation: CroSppCollapsedPackagesVariant | undefined,
  roomPosition: number,
): CroSppCollapsedPackagesVariant {
  if (variation === CroSppCollapsedPackagesVariant.collapsed) {
    return CroSppCollapsedPackagesVariant.collapsed
  }
  if (variation === CroSppCollapsedPackagesVariant.expanded) {
    return roomPosition === 1 ? CroSppCollapsedPackagesVariant.expanded : CroSppCollapsedPackagesVariant.collapsed
  }
  return CroSppCollapsedPackagesVariant.control
}

function useCroSppCollapsedPackagesExperiment(options?: Options): CroSppCollapsedPackagesExperiment {
  // Activation is handled by the desktop/tablet-only mounted path so mobile users can read
  // the SSR-bucketed variation without contaminating this desktop-only experiment.
  const { variation } = useExperiment(
    Experiments.croSppCollapsedPackagesV2,
    { canActivate: false },
  )
  const packagesVariant = options?.disabled ? undefined : getCroSppCollapsedPackagesVariant(variation)

  const getRoomPackagesVariant = useCallback((roomPosition: number): CroSppCollapsedPackagesVariant => (
    getCroSppCollapsedPackagesRoomVariant(
      packagesVariant,
      roomPosition,
    )
  ), [packagesVariant])

  return {
    getRoomPackagesVariant,
  }
}

export default useCroSppCollapsedPackagesExperiment
